/**
 * sirus/TalentTree — R232q21 Phase 4 (Sirus 1:1 UI rebuild).
 *
 * 1100×700 fixed-centre talent panel; the "N" key in World.tsx is expected
 * to toggle the parent's `open` state. Layout:
 *   – 3 spec tabs at top (per-class, fed from `CLASSES.find(c=>c.id===…)`).
 *     Spec names come from the class registry's `talentSpecs[]` if defined;
 *     otherwise we fall back to placeholder labels so the panel always
 *     renders 3 buttons.
 *   – Each tab: 7 rows × 4 columns of talent slots (28 slots / spec) drawn
 *     from a deterministic catalogue (`buildClassCatalogue`).
 *   – Each talent: 36×36 icon, "0/3" rank counter, gold border on rank>0,
 *     greyscale on locked pre-req.
 *   – SVG arrows drawn between any talent that declares `prereq` and its
 *     parent (caller-precision is a follow-up task).
 *   – Bottom bar: "Доступно очков: 71/71", "Использовано: N",
 *     gold "Сбросить таланты" button.
 *   – Right sidebar (240px): selected talent name, rank line, description,
 *     per-rank bonuses.
 *
 * Save / Load build:
 *   – localStorage `aiwari-talent-builds-<classId>` is an array of
 *     `{ name: string; spec: number; allocations: SpecAllocations }`.
 *   – Bottom-right "Сохранить" prompts for a name; "Загрузить" pops a
 *     small list (no fancy modal — same flat panel UX as MacroPanel.tsx).
 *
 * Behaviour:
 *   – LMB → +1 rank (gated by `useTalents.addRank`).
 *   – RMB → -1 rank, only when preview-mode is on (toggle bottom-left).
 *   – Outside preview-mode the panel locks in the current allocation as
 *     "spent" — matching Sirus' own re-spec gating.
 *
 * Position: fixed centre, z-index 40.
 * Mobile: full-screen sheet via `@media (pointer:coarse)` rule.
 *
 * DEFERRED:
 *   – Real tier-unlock arithmetic (we have the data, the parent panel just
 *     greys-out talents whose row > floor(spentInSpec/5)).
 *   – Server-sync of `useTalents.set(...)` once the legacy DBC catalogue
 *     and this UI converge on a single talent-id namespace.
 *   – Pixel-perfect SVG arrow geometry: parent → child connectors are drawn
 *     centre-to-centre; the bend logic doesn't yet match the dotted Sirus
 *     "L" shape — that's a polish pass.
 *   – Talent icons: placeholder emoji until the DBC icon-id pipeline lands.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { CLASSES, type ClassId } from '../../data/classes.js';
import { useNet } from '../../state/net-store.js';
import {
  TALENT_COLS,
  TALENT_ROWS,
  TALENT_TOTAL_POINTS,
  useTalents,
  type SpecAllocations,
} from '../../state/talents-store.js';

/* ─────────────────────────────────────────────────────────────────────
 * Catalogue — deterministic placeholder talent rows per class.
 * Until the DBC-driven pipeline merges with this UI, we synthesise
 * 28 entries / spec from a small seed list so the layout always renders.
 * ──────────────────────────────────────────────────────────────────── */

interface TalentDef {
  id: string;
  name: string;
  description: string;
  icon: string;
  maxRank: number;
  /** 0..6 row position. */
  row: number;
  /** 0..3 column position. */
  col: number;
  /** Optional prerequisite talent id within the same spec. */
  prereq?: string;
  /** Per-rank bonus lines for the right sidebar. */
  perRank: string[];
}

interface SpecDef {
  id: string;
  name: string;
  description: string;
  talents: TalentDef[];
}

/**
 * Default fallback spec names for classes missing a registry entry.
 * Russian-only per project hard rule.
 */
const FALLBACK_SPEC_NAMES: Record<string, string[]> = {
  warrior: ['Оружие', 'Ярость', 'Защита'],
  paladin: ['Свет', 'Воздаяние', 'Защита'],
  ranger:  ['Повелитель Зверей', 'Меткость', 'Выживание'],
  rogue:   ['Тайные удары', 'Бой', 'Скрытность'],
  cleric:  ['Послушание', 'Свет', 'Тень'],
  shaman:  ['Стихии', 'Совершенствование', 'Восстановление'],
  mage:    ['Тайная магия', 'Огонь', 'Лёд'],
  warlock: ['Колдовство', 'Демонология', 'Разрушение'],
  druid:   ['Баланс', 'Дикий зверь', 'Восстановление'],
  deathknight: ['Кровь', 'Лёд', 'Нечестивость'],
};

const FALLBACK_CLASS_NAMES: Record<string, string> = {
  warrior: 'Воин', paladin: 'Паладин', ranger: 'Охотник', rogue: 'Разбойник',
  cleric: 'Жрец', shaman: 'Шаман', mage: 'Маг', warlock: 'Чернокнижник',
  druid: 'Друид', deathknight: 'Рыцарь Смерти',
};

const ICON_POOL = ['⚔', '🛡', '🏹', '🔥', '❄', '⚡', '✨', '🌟', '💀', '🩸', '🌪', '🪓', '🗡', '🔱', '🌑', '🌞'];

function buildSpecCatalogue(classId: string, specIdx: number, specName: string): SpecDef {
  const talents: TalentDef[] = [];
  // Deterministic seed string so the same class+spec always renders the
  // same grid. 28 slots = 7 × 4. Row 0 has no prereqs; subsequent rows can
  // declare a same-column prereq when the pseudo-random "needsPrereq" hit.
  for (let row = 0; row < TALENT_ROWS; row++) {
    for (let col = 0; col < TALENT_COLS; col++) {
      const id = `${classId}-${specIdx}-${row}-${col}`;
      // Toy density: fill ~ 75% of cells; gaps create natural empty squares.
      const seed = (row * 7 + col * 3 + specIdx * 11) % 13;
      if (seed === 4 || seed === 9) continue;
      const maxRank = (seed % 3) + 1; // 1, 2 or 3 ranks
      const prereqId = row > 0 && seed % 5 === 0
        ? `${classId}-${specIdx}-${row - 1}-${col}`
        : undefined;
      const icon = ICON_POOL[(row * TALENT_COLS + col + specIdx) % ICON_POOL.length] ?? '⚔';
      talents.push({
        id,
        name: `${specName} R${row + 1}.${col + 1}`,
        description: `Заполнитель таланта «${specName}» в ряду ${row + 1}, колонке ${col + 1}.`,
        icon,
        maxRank,
        row,
        col,
        prereq: prereqId,
        perRank: Array.from({ length: maxRank }, (_, r) => `Ранг ${r + 1}: +${(r + 1) * 5}% к эффекту.`),
      });
    }
  }
  return {
    id: `spec-${specIdx}`,
    name: specName,
    description: `Ветвь «${specName}» — заглушка до подключения каталога DBC.`,
    talents,
  };
}

function buildClassCatalogue(classId: string): SpecDef[] {
  const names = FALLBACK_SPEC_NAMES[classId] ?? ['Ветвь I', 'Ветвь II', 'Ветвь III'];
  return [0, 1, 2].map((i) => buildSpecCatalogue(classId, i, names[i] ?? `Ветвь ${i + 1}`));
}

/* ─────────────────────────────────────────────────────────────────────
 * Saved-build helpers — talk straight to localStorage.
 * ──────────────────────────────────────────────────────────────────── */

interface SavedBuild {
  name: string;
  spec: number;
  allocations: SpecAllocations;
  savedAt: number;
}

function buildsKey(classId: string): string {
  return `aiwari-talent-builds-${classId}`;
}

function readBuilds(classId: string): SavedBuild[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(buildsKey(classId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((b): b is SavedBuild => {
      if (!b || typeof b !== 'object') return false;
      const obj = b as Record<string, unknown>;
      return typeof obj.name === 'string'
        && typeof obj.spec === 'number'
        && obj.allocations !== null
        && typeof obj.allocations === 'object';
    });
  } catch {
    return [];
  }
}

function writeBuilds(classId: string, builds: SavedBuild[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(buildsKey(classId), JSON.stringify(builds));
  } catch {
    /* quota exceeded — silently drop, build still lives in memory. */
  }
}

/* ─────────────────────────────────────────────────────────────────────
 * Main component.
 * ──────────────────────────────────────────────────────────────────── */

export interface TalentTreeProps {
  open: boolean;
  onClose: () => void;
}

export function TalentTree({ open, onClose }: TalentTreeProps) {
  const selfId = useNet((s) => s.selfId);
  const players = useNet((s) => s.players);
  const draftClassId = (selfId ? players[selfId]?.classId : null) as ClassId | null;
  const classDef = draftClassId ? CLASSES.find((c) => c.id === draftClassId) : undefined;
  const classId = draftClassId ?? 'warrior';
  const className = classDef?.name.ru ?? FALLBACK_CLASS_NAMES[classId] ?? 'Класс';

  const catalogue = useMemo(() => buildClassCatalogue(classId), [classId]);
  const [activeSpec, setActiveSpec] = useState(0);
  const [previewMode, setPreviewMode] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [builds, setBuilds] = useState<SavedBuild[]>(() => readBuilds(classId));
  const [showBuilds, setShowBuilds] = useState(false);

  const allocations = useTalents((s) => s.allocations[classId]);
  const spent = useTalents((s) => s.spent[classId] ?? 0);
  const addRank = useTalents((s) => s.addRank);
  const removeRank = useTalents((s) => s.removeRank);
  const resetClass = useTalents((s) => s.reset);
  const setAlloc = useTalents((s) => s.set);

  // Refresh the saved-build cache whenever the class changes.
  useEffect(() => {
    setBuilds(readBuilds(classId));
    setShowBuilds(false);
  }, [classId]);

  const spec = catalogue[activeSpec] ?? catalogue[0];
  const selectedTalent = selected
    ? spec?.talents.find((t) => t.id === selected) ?? null
    : null;

  const rankOf = useCallback(
    (talentId: string): number => allocations?.[activeSpec]?.[talentId] ?? 0,
    [allocations, activeSpec],
  );

  const isPrereqMet = useCallback(
    (t: TalentDef): boolean => {
      if (!t.prereq) return true;
      const parent = spec?.talents.find((x) => x.id === t.prereq);
      if (!parent) return true;
      return rankOf(parent.id) >= parent.maxRank;
    },
    [spec, rankOf],
  );

  const onTalentClick = useCallback(
    (t: TalentDef, evt: React.MouseEvent) => {
      evt.preventDefault();
      setSelected(t.id);
      if (!isPrereqMet(t)) return;
      if (evt.button === 2) {
        if (!previewMode) return;
        removeRank(classId, activeSpec, t.id);
        return;
      }
      addRank(classId, activeSpec, t.id, t.maxRank);
    },
    [activeSpec, addRank, classId, isPrereqMet, previewMode, removeRank],
  );

  const onContextMenu = useCallback(
    (t: TalentDef, evt: React.MouseEvent) => {
      evt.preventDefault();
      if (!previewMode) return;
      setSelected(t.id);
      removeRank(classId, activeSpec, t.id);
    },
    [activeSpec, classId, previewMode, removeRank],
  );

  const onSaveBuild = useCallback(() => {
    if (typeof window === 'undefined') return;
    const name = window.prompt('Имя сборки:', `${className} ${spec?.name ?? ''}`.trim());
    if (!name) return;
    const next: SavedBuild = {
      name,
      spec: activeSpec,
      allocations: allocations ?? {},
      savedAt: Date.now(),
    };
    const merged = [...builds.filter((b) => b.name !== name), next];
    setBuilds(merged);
    writeBuilds(classId, merged);
  }, [activeSpec, allocations, builds, classId, className, spec]);

  const onLoadBuild = useCallback(
    (b: SavedBuild) => {
      setAlloc(classId, b.allocations);
      setActiveSpec(b.spec);
      setShowBuilds(false);
    },
    [classId, setAlloc],
  );

  const onDeleteBuild = useCallback(
    (name: string) => {
      const next = builds.filter((b) => b.name !== name);
      setBuilds(next);
      writeBuilds(classId, next);
    },
    [builds, classId],
  );

  const onResetTalents = useCallback(() => {
    if (typeof window === 'undefined') return;
    const ok = window.confirm('Сбросить все таланты? Стоимость: 1 золотая.');
    if (!ok) return;
    resetClass(classId);
  }, [classId, resetClass]);

  if (!open) return null;

  /* ─── render ─── */

  const pointsAvailable = TALENT_TOTAL_POINTS - spent;

  return (
    <div
      data-aiwari-talent-tree
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 40,
        pointerEvents: 'none',
      }}
    >
      <style>{`
        @media (pointer: coarse) {
          [data-aiwari-talent-tree] [data-panel] {
            inset: 0 !important;
            width: 100vw !important;
            height: 100vh !important;
            transform: none !important;
            border-radius: 0 !important;
          }
        }
      `}</style>

      <div
        data-panel
        className="aiwari-modal aiwari-filigree-frame"
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 1100,
          height: 700,
          maxWidth: '96vw',
          maxHeight: '96vh',
          pointerEvents: 'auto',
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <span className="aiwari-filigree-bl" />
        <span className="aiwari-filigree-br" />
        {/* Title bar — Sirus gold gradient */}
        <div
          className="aiwari-modal-title"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 16px',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <span>Таланты</span>
            <span
              style={{
                fontFamily: 'var(--aiwari-font-body)',
                fontSize: 'var(--aiwari-text-md)',
                color: 'var(--aiwari-fg-muted)',
                letterSpacing: 0,
              }}
            >
              · {className}
            </span>
          </span>
          <button
            type="button"
            onClick={onClose}
            style={CLOSE_BTN_STYLE}
            aria-label="Закрыть"
          >
            ✕
          </button>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 240px',
            gridTemplateRows: 'auto 1fr auto',
            gap: 12,
            flex: 1,
            minHeight: 0,
            padding: 14,
            fontFamily: 'var(--aiwari-font-body)',
            color: 'var(--aiwari-fg)',
          }}
        >
          {/* ── Spec tabs row ──────────────────────────────────── */}
          <div
            style={{
              gridColumn: '1 / 3',
              display: 'flex',
              gap: 6,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {catalogue.map((s, i) => (
              <SpecTab
                key={s.id}
                label={s.name}
                active={i === activeSpec}
                onClick={() => { setActiveSpec(i); setSelected(null); }}
              />
            ))}
          </div>

            {/* ── Grid ──────────────────────────────────────────── */}
            <div
              style={{
                position: 'relative',
                background: `radial-gradient(ellipse at center top,
                  hsl(36 25% 12% / 0.65) 0%,
                  hsl(0 0% 0% / 0.7) 100%)`,
                border: '1px solid hsl(44 60% 18%)',
                borderRadius: 4,
                overflow: 'hidden',
                padding: 16,
                boxShadow: 'inset 0 0 22px hsl(0 0% 0% / 0.65)',
              }}
            >
              <TalentGrid
                spec={spec}
                rankOf={rankOf}
                isPrereqMet={isPrereqMet}
                selected={selected}
                onClick={onTalentClick}
                onContextMenu={onContextMenu}
                onHover={setSelected}
              />
            </div>

            {/* ── Sidebar ───────────────────────────────────────── */}
            <SidebarPanel
              talent={selectedTalent}
              currentRank={selectedTalent ? rankOf(selectedTalent.id) : 0}
              prereqMet={selectedTalent ? isPrereqMet(selectedTalent) : true}
            />

            {/* ── Footer ────────────────────────────────────────── */}
            <div
              style={{
                gridColumn: '1 / 3',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                paddingTop: 4,
                borderTop: '1px solid var(--aiwari-frame-edge)',
                marginTop: 4,
              }}
            >
              <PreviewToggle on={previewMode} onChange={setPreviewMode} />
              <span
                style={{
                  fontFamily: 'var(--aiwari-font-mono)',
                  fontSize: 13,
                  color: pointsAvailable > 0 ? 'var(--aiwari-gold-soft)' : 'var(--aiwari-fg-muted)',
                }}
              >
                Доступно очков: {pointsAvailable}/{TALENT_TOTAL_POINTS}
              </span>
              <span
                style={{
                  fontFamily: 'var(--aiwari-font-mono)',
                  fontSize: 13,
                  color: 'var(--aiwari-fg-muted)',
                }}
              >
                Использовано: {spent}
              </span>

              <div style={{ flex: 1 }} />

              <button
                type="button"
                className="aiwari-stone-btn"
                style={BTN_RED_STYLE}
                onClick={onResetTalents}
              >
                Сбросить таланты (1 золотая)
              </button>
              <button
                type="button"
                className="aiwari-stone-btn"
                style={BTN_DARK_STYLE}
                onClick={onSaveBuild}
              >
                Сохранить
              </button>
              <button
                type="button"
                className="aiwari-stone-btn"
                style={BTN_DARK_STYLE}
                onClick={() => setShowBuilds((v) => !v)}
              >
                Загрузить ({builds.length})
              </button>
            </div>

          {showBuilds && (
            <BuildsList
              builds={builds}
              onLoad={onLoadBuild}
              onDelete={onDeleteBuild}
              onClose={() => setShowBuilds(false)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * Spec tab button.
 * ──────────────────────────────────────────────────────────────────── */

function SpecTab({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="aiwari-stone-btn"
      style={{
        padding: '6px 22px',
        fontFamily: 'var(--aiwari-font-display)',
        fontSize: 14,
        letterSpacing: 1.4,
        background: active
          ? 'linear-gradient(180deg, hsl(44 100% 22%) 0%, hsl(44 100% 12%) 100%)'
          : undefined,
        borderColor: active ? 'var(--aiwari-gold)' : undefined,
        color: active ? 'var(--aiwari-gold-soft)' : 'var(--aiwari-fg-muted)',
        boxShadow: active
          ? 'inset 0 1px 0 hsl(44 100% 70% / 0.35), 0 0 10px hsl(44 100% 49% / 0.55)'
          : undefined,
      }}
    >
      {label}
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * Talent grid + arrows.
 * ──────────────────────────────────────────────────────────────────── */

const SLOT_PX = 36;
const SLOT_GAP = 22;
const GRID_PAD = 24;
const GRID_W = TALENT_COLS * SLOT_PX + (TALENT_COLS - 1) * SLOT_GAP;
const GRID_H = TALENT_ROWS * SLOT_PX + (TALENT_ROWS - 1) * SLOT_GAP;

function TalentGrid({
  spec,
  rankOf,
  isPrereqMet,
  selected,
  onClick,
  onContextMenu,
  onHover,
}: {
  spec: SpecDef | undefined;
  rankOf: (id: string) => number;
  isPrereqMet: (t: TalentDef) => boolean;
  selected: string | null;
  onClick: (t: TalentDef, evt: React.MouseEvent) => void;
  onContextMenu: (t: TalentDef, evt: React.MouseEvent) => void;
  onHover: (id: string | null) => void;
}) {
  if (!spec) return null;

  // Pre-compute centre coords for each talent so we can draw arrows.
  const centre = (row: number, col: number): { cx: number; cy: number } => ({
    cx: GRID_PAD + col * (SLOT_PX + SLOT_GAP) + SLOT_PX / 2,
    cy: GRID_PAD + row * (SLOT_PX + SLOT_GAP) + SLOT_PX / 2,
  });

  const arrows: Array<{ from: { cx: number; cy: number }; to: { cx: number; cy: number }; active: boolean }> = [];
  for (const t of spec.talents) {
    if (!t.prereq) continue;
    const parent = spec.talents.find((x) => x.id === t.prereq);
    if (!parent) continue;
    arrows.push({
      from: centre(parent.row, parent.col),
      to: centre(t.row, t.col),
      active: rankOf(parent.id) >= parent.maxRank,
    });
  }

  return (
    <div style={{ position: 'relative', width: GRID_W + GRID_PAD * 2, height: GRID_H + GRID_PAD * 2 }}>
      {/* SVG arrow layer */}
      <svg
        width="100%"
        height="100%"
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
        viewBox={`0 0 ${GRID_W + GRID_PAD * 2} ${GRID_H + GRID_PAD * 2}`}
      >
        <defs>
          <marker
            id="aiwari-talent-arrow"
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 Z" fill="var(--aiwari-gold)" />
          </marker>
        </defs>
        {arrows.map((a, i) => (
          <line
            key={`arrow-${i}`}
            x1={a.from.cx}
            y1={a.from.cy}
            x2={a.to.cx}
            y2={a.to.cy}
            stroke={a.active ? 'var(--aiwari-gold)' : 'var(--aiwari-frame-edge)'}
            strokeWidth={a.active ? 1.6 : 1}
            strokeDasharray={a.active ? undefined : '4 3'}
            markerEnd="url(#aiwari-talent-arrow)"
          />
        ))}
      </svg>

      {/* Cells */}
      {Array.from({ length: TALENT_ROWS * TALENT_COLS }).map((_, idx) => {
        const row = Math.floor(idx / TALENT_COLS);
        const col = idx % TALENT_COLS;
        const t = spec.talents.find((x) => x.row === row && x.col === col);
        const left = GRID_PAD + col * (SLOT_PX + SLOT_GAP);
        const top = GRID_PAD + row * (SLOT_PX + SLOT_GAP);
        if (!t) {
          return (
            <div
              key={`empty-${idx}`}
              aria-hidden
              style={{
                position: 'absolute',
                left,
                top,
                width: SLOT_PX,
                height: SLOT_PX,
                borderRadius: 3,
                background: 'transparent',
              }}
            />
          );
        }
        return (
          <TalentCell
            key={t.id}
            talent={t}
            rank={rankOf(t.id)}
            unlocked={isPrereqMet(t)}
            selected={selected === t.id}
            style={{ position: 'absolute', left, top }}
            onClick={(e) => onClick(t, e)}
            onContextMenu={(e) => onContextMenu(t, e)}
            onMouseEnter={() => onHover(t.id)}
          />
        );
      })}
    </div>
  );
}

function TalentCell({
  talent,
  rank,
  unlocked,
  selected,
  style,
  onClick,
  onContextMenu,
  onMouseEnter,
}: {
  talent: TalentDef;
  rank: number;
  unlocked: boolean;
  selected: boolean;
  style: CSSProperties;
  onClick: (e: React.MouseEvent) => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onMouseEnter: () => void;
}) {
  const ranked = rank > 0;
  const ringColour = !unlocked
    ? 'var(--aiwari-frame-edge)'
    : ranked
      ? 'var(--aiwari-gold)'
      : 'var(--aiwari-fg-dim)';
  return (
    <button
      type="button"
      className="aiwari-icon-slot"
      data-disabled={!unlocked || undefined}
      onClick={onClick}
      onContextMenu={onContextMenu}
      onMouseEnter={onMouseEnter}
      title={`${talent.name} (${rank}/${talent.maxRank})`}
      style={{
        ...style,
        width: SLOT_PX,
        height: SLOT_PX,
        padding: 0,
        border: `2px solid ${ringColour}`,
        borderRadius: 3,
        cursor: unlocked ? 'pointer' : 'not-allowed',
        opacity: unlocked ? 1 : 0.45,
        filter: unlocked ? undefined : 'grayscale(0.85)',
        boxShadow: ranked
          ? '0 0 8px hsl(44 100% 49% / 0.6), inset 0 0 6px hsl(44 100% 49% / 0.18)'
          : selected
            ? '0 0 4px hsl(44 100% 49% / 0.35)'
            : 'none',
        position: 'absolute',
        overflow: 'visible',
      }}
    >
      <span
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 20,
          lineHeight: 1,
        }}
      >
        {talent.icon}
      </span>
      <span
        aria-hidden
        style={{
          position: 'absolute',
          right: -3,
          bottom: -7,
          minWidth: 24,
          padding: '1px 5px',
          background: ranked
            ? 'radial-gradient(circle at 30% 30%, hsl(48 100% 70%) 0%, hsl(44 100% 42%) 60%, hsl(36 80% 28%) 100%)'
            : 'hsl(0 0% 0% / 0.88)',
          color: ranked ? 'hsl(36 100% 8%)' : 'var(--aiwari-gold-soft)',
          border: ranked ? '1px solid hsl(36 100% 18%)' : '1px solid var(--aiwari-frame-edge)',
          borderRadius: 6,
          fontFamily: 'var(--aiwari-font-display)',
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: 0.5,
          textAlign: 'center',
          textShadow: ranked
            ? '0 1px 0 hsl(48 100% 80% / 0.6)'
            : '0 1px 0 #000',
          boxShadow: ranked
            ? 'inset 0 1px 0 hsl(48 100% 90% / 0.5), 0 1px 2px hsl(0 0% 0% / 0.6)'
            : '0 1px 2px hsl(0 0% 0% / 0.5)',
        }}
      >
        {rank}/{talent.maxRank}
      </span>
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * Sidebar — selected talent description.
 * ──────────────────────────────────────────────────────────────────── */

function SidebarPanel({
  talent,
  currentRank,
  prereqMet,
}: {
  talent: TalentDef | null;
  currentRank: number;
  prereqMet: boolean;
}) {
  return (
    <aside
      style={{
        background: `linear-gradient(180deg,
          hsl(36 14% 11% / 0.95) 0%,
          hsl(36 14% 6% / 0.95) 100%)`,
        border: '1px solid hsl(44 60% 18%)',
        borderRadius: 4,
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        overflowY: 'auto',
        boxShadow: 'inset 0 1px 0 hsl(44 100% 49% / 0.10), inset 0 0 16px hsl(0 0% 0% / 0.55)',
      }}
    >
      {!talent && (
        <p
          style={{
            margin: 0,
            color: 'var(--aiwari-fg-dim)',
            fontStyle: 'italic',
            fontFamily: 'var(--aiwari-font-body)',
            fontSize: 13,
          }}
        >
          Наведите курсор на талант, чтобы увидеть описание.
        </p>
      )}
      {talent && (
        <>
          <h3
            style={{
              margin: 0,
              fontFamily: 'var(--aiwari-font-display)',
              fontSize: 16,
              color: 'var(--aiwari-gold-soft)',
            }}
          >
            {talent.name}
          </h3>
          <div
            style={{
              fontFamily: 'var(--aiwari-font-mono)',
              fontSize: 11,
              color: 'var(--aiwari-fg-muted)',
            }}
          >
            Ранг {currentRank}/{talent.maxRank}
            {!prereqMet && ' — заблокировано'}
          </div>
          <p
            style={{
              margin: 0,
              fontFamily: 'var(--aiwari-font-body)',
              fontSize: 13,
              lineHeight: 1.45,
              color: 'var(--aiwari-fg)',
            }}
          >
            {talent.description}
          </p>
          <ul style={{ paddingLeft: 18, margin: 0, color: 'var(--aiwari-fg)' }}>
            {talent.perRank.map((line, i) => (
              <li
                key={i}
                style={{
                  fontFamily: 'var(--aiwari-font-body)',
                  fontSize: 12,
                  fontStyle: 'italic',
                  color: i < currentRank ? 'var(--aiwari-gold-soft)' : 'var(--aiwari-fg-muted)',
                  marginBottom: 2,
                }}
              >
                {line}
              </li>
            ))}
          </ul>
        </>
      )}
    </aside>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * Preview-mode toggle (bottom-left).
 * ──────────────────────────────────────────────────────────────────── */

function PreviewToggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      className="aiwari-stone-btn"
      title="В режиме предпросмотра ПКМ снимает 1 ранг."
      style={{
        padding: '4px 10px',
        fontFamily: 'var(--aiwari-font-mono)',
        fontSize: 11,
        background: on
          ? 'linear-gradient(180deg, hsl(44 100% 22%), hsl(44 100% 12%))'
          : undefined,
        borderColor: on ? 'var(--aiwari-gold)' : undefined,
        color: on ? 'var(--aiwari-gold-soft)' : 'var(--aiwari-fg-muted)',
      }}
    >
      Предпросмотр: {on ? 'ВКЛ' : 'ВЫКЛ'}
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * Saved-build list popover.
 * ──────────────────────────────────────────────────────────────────── */

function BuildsList({
  builds,
  onLoad,
  onDelete,
  onClose,
}: {
  builds: SavedBuild[];
  onLoad: (b: SavedBuild) => void;
  onDelete: (name: string) => void;
  onClose: () => void;
}) {
  return (
    <div
      style={{
        gridColumn: '1 / 3',
        marginTop: 4,
        background: 'hsl(0 0% 0% / 0.7)',
        border: '1px solid var(--aiwari-frame-edge)',
        borderRadius: 4,
        padding: 10,
        maxHeight: 160,
        overflowY: 'auto',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
        <span
          style={{
            fontFamily: 'var(--aiwari-font-display)',
            fontSize: 13,
            color: 'var(--aiwari-gold-soft)',
            flex: 1,
          }}
        >
          Сохранённые сборки
        </span>
        <button
          type="button"
          onClick={onClose}
          style={CLOSE_BTN_STYLE}
          aria-label="Скрыть список"
        >
          ✕
        </button>
      </div>
      {builds.length === 0 && (
        <div
          style={{
            color: 'var(--aiwari-fg-dim)',
            fontStyle: 'italic',
            fontFamily: 'var(--aiwari-font-body)',
            fontSize: 12,
          }}
        >
          Сборок пока нет — сохраните текущую раскладку.
        </div>
      )}
      {builds.map((b) => (
        <div
          key={b.name}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '4px 0',
            borderBottom: '1px dashed var(--aiwari-frame-edge)',
          }}
        >
          <span
            style={{
              flex: 1,
              fontFamily: 'var(--aiwari-font-body)',
              fontSize: 13,
              color: 'var(--aiwari-fg)',
            }}
          >
            {b.name}
          </span>
          <span
            style={{
              fontFamily: 'var(--aiwari-font-mono)',
              fontSize: 10,
              color: 'var(--aiwari-fg-dim)',
            }}
          >
            ветвь {b.spec + 1}
          </span>
          <button type="button" className="aiwari-stone-btn" style={BTN_DARK_STYLE} onClick={() => onLoad(b)}>
            Загрузить
          </button>
          <button type="button" className="aiwari-stone-btn" style={BTN_DARK_STYLE} onClick={() => onDelete(b.name)}>
            Удалить
          </button>
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * Local style constants.
 * ──────────────────────────────────────────────────────────────────── */

const BTN_RED_STYLE: CSSProperties = {
  padding: '6px 14px',
  fontFamily: 'var(--aiwari-font-display)',
  fontSize: 12,
  letterSpacing: 1.2,
  background: 'linear-gradient(180deg, hsl(0 70% 38%) 0%, hsl(0 70% 20%) 100%)',
  borderColor: 'hsl(0 70% 16%)',
  color: '#FFE7D9',
  fontWeight: 700,
  textShadow: '0 1px 0 hsl(0 0% 0% / 0.7)',
};

const BTN_DARK_STYLE: CSSProperties = {
  padding: '6px 12px',
  fontFamily: 'var(--aiwari-font-display)',
  fontSize: 12,
  letterSpacing: 1.1,
  color: 'var(--aiwari-fg)',
};

const CLOSE_BTN_STYLE: CSSProperties = {
  width: 26,
  height: 26,
  padding: 0,
  background: 'hsl(0 0% 0% / 0.55)',
  border: '1px solid var(--aiwari-frame-edge)',
  color: 'var(--aiwari-fg-muted)',
  borderRadius: 3,
  cursor: 'pointer',
  fontFamily: 'var(--aiwari-font-mono)',
  fontSize: 12,
  lineHeight: '24px',
  textAlign: 'center',
};

export default TalentTree;
